import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './UnifiedAdminDashboard.css';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Mail, 
  Phone, 
  Building, 
  Eye, 
  Edit, 
  Trash2, 
  Search,
  CheckCircle,
  Clock,
  Archive,
  User,
  MessageSquare,
  RefreshCw,
  Download,
  Settings,
  Plus,
  Upload,
  Image,
  Globe,
  TrendingUp,
  Car,
  MapPin,
  BookOpen,
  Heart,
  Wifi,
  Users,
  BarChart3,
  Database,
  FileSpreadsheet,
  Map,
  Languages,
  Palette,
  Home,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { seedAPI, excelAPI, kmlAPI, contactAPI } from '../../services/api';
import { useContent, SlideItem } from '../../contexts/ContentContext';
// Lazy load the investment opportunity management component
const InvestmentOpportunityManagement = React.lazy(() => import('../../components/InvestmentOpportunityManagement'));

interface ContactSubmission {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  company?: string;
  position?: string;
  message: string;
  status: 'new' | 'contacted' | 'in_progress' | 'resolved' | 'archived';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notes?: string;
  assignedTo?: {
    _id: string;
    username: string;
    email: string;
  };
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  contactedAt?: string;
  resolvedAt?: string;
}

interface ContactStats {
  total: number;
  unread: number;
  new: number;
  byStatus: Record<string, number>;
}

interface ContactInfo {
  office: string;
  email: string;
  phone: string;
  whatsapp?: string;
  address: string;
  officeHours: string;
  description: string;
}

const SimpleUnifiedDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const {
    slides,
    facilities,
    investmentOpportunities,
    footerContent,
    websiteSettings,
    profileContent,
    updateSlide,
    deleteSlide,
    toggleSlide,
    addSlide,
    updateFooterContent,
    updateWebsiteSettings,
    updateProfileContent,
    addAdvantage,
    updateAdvantage,
    deleteAdvantage,
    addFacility,
    updateFacility,
    deleteFacility,
    toggleFacility,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    toggleOpportunity,
    refreshContent,
  } = useContent();

  // State management
  const [activeSection, setActiveSection] = useState<'overview' | 'content' | 'contacts' | 'data'>('overview');
  
  // Debug logging for content context data
  useEffect(() => {
    console.log('📊 Dashboard Content Data:');
    console.log('📊 Slides:', slides?.length || 0);
    console.log('📊 Facilities:', facilities?.length || 0);
    console.log('📊 Investment Opportunities:', investmentOpportunities?.length || 0);
    console.log('📊 Profile Content:', profileContent?.title);
    console.log('📊 Footer Content:', footerContent?.description);
  }, [slides, facilities, investmentOpportunities, profileContent, footerContent]);
  const [activeTab, setActiveTab] = useState<'slider' | 'profile' | 'facilities' | 'opportunities' | 'footer' | 'settings'>('slider');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResults, setImportResults] = useState<any>(null);
  const [selectedKMLFile, setSelectedKMLFile] = useState<File | null>(null);
  const [showContentDropdown, setShowContentDropdown] = useState(false);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [showAddAdvantageModal, setShowAddAdvantageModal] = useState(false);
  const [showEditSlideModal, setShowEditSlideModal] = useState(false);
  const [showAddOpportunityModal, setShowAddOpportunityModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState<any>(null);
  const [facilityForm, setFacilityForm] = useState({
    id: 0,
    name: '',
    description: '',
    icon: 'fas fa-building',
    category: 'Umum',
    isActive: true
  });
  const [advantageForm, setAdvantageForm] = useState({
    id: 0,
    icon: 'fas fa-map-marker-alt',
    title: '',
    description: ''
  });
  const [opportunityForm, setOpportunityForm] = useState({
    id: 0,
    title: '',
    description: '',
    image: '',
    sector: '',
    investmentRange: '',
    roi: '',
    location: '',
    isActive: true
  });
  const [slideForm, setSlideForm] = useState({
    id: 0,
    title: {
      id: '',
      en: ''
    },
    subtitle: {
      id: '',
      en: ''
    },
    image: '',
    order: 1,
    isActive: true
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [operatingHours, setOperatingHours] = useState([
    { id: 1, day: 'Senin - Jumat', startTime: '08:00', endTime: '16:00', isActive: true },
    { id: 2, day: 'Sabtu', startTime: '08:00', endTime: '12:00', isActive: true },
    { id: 3, day: 'Minggu', startTime: '', endTime: '', isActive: false }
  ]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  
  // Contact management state
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  
  // Filters and search for contacts
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });

  // Contact info form state
  const [contactInfoForm, setContactInfoForm] = useState<ContactInfo>({
    office: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    officeHours: '',
    description: ''
  });

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Local state for all content to prevent auto-save
  const [localProfileContent, setLocalProfileContent] = useState(profileContent);
  const [localFooterContent, setLocalFooterContent] = useState(footerContent);
  const [localWebsiteSettings, setLocalWebsiteSettings] = useState(websiteSettings);
  const [localSlides, setLocalSlides] = useState<SlideItem[]>(() => {
    try {
      const savedData = localStorage.getItem('sukoharjo-content-data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        console.log('🟢 Loading slides from localStorage:', parsedData.slides);
        return parsedData.slides || slides || [];
      }
    } catch (error) {
      console.error('Error loading slides from localStorage:', error);
    }
    console.log('🟢 Using context slides as fallback:', slides);
    return slides || [];
  });
  const [localFacilities, setLocalFacilities] = useState(facilities || []);
  
  // Unsaved changes tracking
  const [hasUnsavedProfileChanges, setHasUnsavedProfileChanges] = useState(false);
  const [hasUnsavedFooterChanges, setHasUnsavedFooterChanges] = useState(false);
  const [hasUnsavedSettingsChanges, setHasUnsavedSettingsChanges] = useState(false);
  const [hasUnsavedSlideChanges, setHasUnsavedSlideChanges] = useState(false);
  const [hasUnsavedFacilityChanges, setHasUnsavedFacilityChanges] = useState(false);
  const isSavingSlides = useRef(false);

  // Button styles
  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textDecoration: 'none',
    border: 'none'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#2563eb',
    color: 'white'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6b7280',
    color: 'white'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#dc2626',
    color: 'white'
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };
  
  // Basic component structure with proper JSX
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: 'white', 
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', 
        borderBottom: '1px solid #e5e7eb' 
      }}>
        <div style={{ padding: '1rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img
                src={websiteSettings?.adminLogo || websiteSettings?.logo || '/logo192.png'}
                alt={websiteSettings?.siteName || 'Website Logo'}
                style={{ height: '40px', width: 'auto', borderRadius: '0.375rem' }}
              />
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0 0 0.25rem 0' }}>
                Admin Dashboard
              </h1>
              <p style={{ color: '#6b7280', margin: '0' }}>
                Kelola konten website dan administrasi sistem
              </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Selamat datang, {user?.email}</span>
              <button
                onClick={logout}
                style={{ ...dangerButtonStyle }}
              >
                Keluar
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Message Banner */}
      {message && (
        <div style={{ 
          padding: '0.75rem 1.5rem',
          backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: message.type === 'success' ? '#166534' : '#991b1b'
        }}>
          <p style={{ fontSize: '0.875rem', fontWeight: '500', margin: '0' }}>{message.text}</p>
        </div>
      )}

      <div style={{ display: 'flex' }}>
        {/* Sidebar Navigation */}
        <nav style={{ 
          width: '16rem', 
          backgroundColor: 'white', 
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', 
          borderRight: '1px solid #e5e7eb',
          minHeight: '100vh' 
        }}>
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={() => setActiveSection('overview')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textDecoration: 'none',
                  color: activeSection === 'overview' ? '#1d4ed8' : '#6b7280',
                  backgroundColor: activeSection === 'overview' ? '#dbeafe' : 'transparent'
                }}
              >
                <BarChart3 style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' }} />
                Ringkasan
              </button>
              
              {/* Content Management Dropdown */}
              <div>
                <button
                  onClick={() => {
                    if (activeSection === 'content') {
                      setShowContentDropdown(!showContentDropdown);
                    } else {
                      setActiveSection('content');
                      setShowContentDropdown(true);
                    }
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textDecoration: 'none',
                    color: activeSection === 'content' ? '#1d4ed8' : '#6b7280',
                    backgroundColor: activeSection === 'content' ? '#dbeafe' : 'transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Palette style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' }} />
                    Manajemen Konten
                  </div>
                  {showContentDropdown ? 
                    <ChevronDown style={{ width: '1rem', height: '1rem' }} /> : 
                    <ChevronRight style={{ width: '1rem', height: '1rem' }} />
                  }
                </button>
                
                {showContentDropdown && activeSection === 'content' && (
                  <div style={{ marginLeft: '2rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {[
                      { id: 'slider', label: 'Hero Slider', icon: Image },
                      { id: 'profile', label: 'Profil & Keunggulan', icon: User },
                      { id: 'facilities', label: 'Fasilitas Pendukung', icon: Building },
                      { id: 'opportunities', label: 'Potensi Investasi', icon: TrendingUp },
                      { id: 'footer', label: 'Konten Footer', icon: Globe },
                      { id: 'settings', label: 'Pengaturan Website', icon: Settings }
                    ].map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            setActiveSection('content');
                            setActiveTab(item.id as any);
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontSize: '0.875rem',
                            color: activeTab === item.id ? '#1d4ed8' : '#6b7280',
                            backgroundColor: activeTab === item.id ? '#e0e7ff' : 'transparent'
                          }}
                        >
                          <IconComponent style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setActiveSection('contacts')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textDecoration: 'none',
                  color: activeSection === 'contacts' ? '#1d4ed8' : '#6b7280',
                  backgroundColor: activeSection === 'contacts' ? '#dbeafe' : 'transparent'
                }}
              >
                <Mail style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' }} />
                Manajemen Kontak
              </button>
              
              <button
                onClick={() => setActiveSection('data')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textDecoration: 'none',
                  color: activeSection === 'data' ? '#1d4ed8' : '#6b7280',
                  backgroundColor: activeSection === 'data' ? '#dbeafe' : 'transparent'
                }}
              >
                <Database style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' }} />
                Manajemen Data
              </button>

              <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
              
              {/* Quick Links */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '0 1rem',
                  marginBottom: '0.25rem'
                }}>
                  Tautan Cepat
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <Link to="/admin/pois" style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textDecoration: 'none',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}>
                    <MapPin style={{ width: '1rem', height: '1rem', marginRight: '0.75rem' }} />
                    Kelola POI
                  </Link>
                  <Link to="/admin/categories" style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textDecoration: 'none',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}>
                    <Building style={{ width: '1rem', height: '1rem', marginRight: '0.75rem' }} />
                    Kategori
                  </Link>
                  <Link to="/admin/content" style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textDecoration: 'none',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}>
                    <Palette style={{ width: '1rem', height: '1rem', marginRight: '0.75rem' }} />
                    Content Management
                  </Link>
                  <Link to="/" style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textDecoration: 'none',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}>
                    <Home style={{ width: '1rem', height: '1rem', marginRight: '0.75rem' }} />
                    Lihat Website
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main style={{ flex: '1', padding: '1.5rem' }}>
          {activeSection === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 1rem 0' }}>
                  Ringkasan Dashboard
                </h2>
              </div>

              {/* Quick Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                {/* Slides Stats */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ padding: '0.5rem', backgroundColor: '#dbeafe', borderRadius: '0.5rem', marginRight: '1rem' }}>
                      <Image style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>Hero Slides</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        {slides?.length || 0}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#10b981', margin: '0' }}>
                        {slides?.filter(s => s.isActive).length || 0} aktif
                      </p>
                    </div>
                  </div>
                </div>

                {/* Facilities Stats */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ padding: '0.5rem', backgroundColor: '#dcfce7', borderRadius: '0.5rem', marginRight: '1rem' }}>
                      <Building style={{ width: '1.5rem', height: '1.5rem', color: '#16a34a' }} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>Fasilitas</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        {facilities?.length || 0}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#10b981', margin: '0' }}>
                        {facilities?.filter(f => f.isActive).length || 0} aktif
                      </p>
                    </div>
                  </div>
                </div>

                {/* Investment Opportunities Stats */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ padding: '0.5rem', backgroundColor: '#fef3c7', borderRadius: '0.5rem', marginRight: '1rem' }}>
                      <TrendingUp style={{ width: '1.5rem', height: '1.5rem', color: '#d97706' }} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>Potensi Investasi</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        {investmentOpportunities?.length || 0}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#10b981', margin: '0' }}>
                        {investmentOpportunities?.filter(i => i.isActive).length || 0} aktif
                      </p>
                    </div>
                  </div>
                </div>

                {/* Profile Advantages Stats */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ padding: '0.5rem', backgroundColor: '#f3e8ff', borderRadius: '0.5rem', marginRight: '1rem' }}>
                      <Users style={{ width: '1.5rem', height: '1.5rem', color: '#9333ea' }} />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>Keunggulan Daerah</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        {profileContent?.advantages?.length || 0}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0' }}>
                        {profileContent?.title ? 'Tersedia' : 'Belum diatur'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Management Section */}
          {activeSection === 'content' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 1rem 0' }}>
                  Manajemen Konten
                </h2>
              </div>

              {/* Content tabs */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', margin: '0' }}>
                    {activeTab === 'slider' && 'Manajemen Hero Slider'}
                    {activeTab === 'profile' && 'Profil & Keunggulan'}
                    {activeTab === 'facilities' && 'Fasilitas Pendukung'}
                    {activeTab === 'opportunities' && 'Potensi Investasi'}
                    {activeTab === 'footer' && 'Konten Footer'}
                    {activeTab === 'settings' && 'Pengaturan Website'}
                  </h3>
                </div>

                {/* Slider Tab */}
                {activeTab === 'slider' && (
                  <div>
                    {/* Add New Slide Button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0' }}>
                        {slides?.length || 0} slide tersedia, {slides?.filter(s => s.isActive).length || 0} aktif
                      </p>
                      <button
                        onClick={() => {
                          setSlideForm({
                            id: Date.now(),
                            title: { id: '', en: '' },
                            subtitle: { id: '', en: '' },
                            image: '',
                            order: (slides?.length || 0) + 1,
                            isActive: true
                          });
                          setEditingSlide(null);
                          setShowEditSlideModal(true);
                        }}
                        style={{ ...primaryButtonStyle }}
                      >
                        <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Tambah Slide Baru
                      </button>
                    </div>

                    {/* Slides List */}
                    {!slides || slides.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '3rem', 
                        backgroundColor: '#f9fafb', 
                        borderRadius: '0.5rem',
                        border: '2px dashed #d1d5db'
                      }}>
                        <Image style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#6b7280' }}>Belum ada slide hero</h3>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#9ca3af' }}>Tambahkan slide pertama untuk memulai</p>
                        <button
                          onClick={() => {
                            setSlideForm({
                              id: Date.now(),
                              title: { id: '', en: '' },
                              subtitle: { id: '', en: '' },
                              image: '',
                              order: 1,
                              isActive: true
                            });
                            setEditingSlide(null);
                            setShowEditSlideModal(true);
                          }}
                          style={{ ...primaryButtonStyle }}
                        >
                          <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          Tambah Slide Pertama
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {slides.map((slide) => (
                          <div key={slide.id} style={{
                            backgroundColor: '#f9fafb',
                            padding: '1rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <div style={{
                                width: '60px',
                                height: '40px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundImage: slide.image ? `url(${slide.image})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}>
                                {!slide.image && <Image style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} />}
                              </div>
                              <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: '500', margin: '0 0 0.25rem 0', color: '#111827' }}>
                                  {slide.title?.id || 'Judul belum diatur'}
                                </h4>
                                <p style={{ fontSize: '0.875rem', margin: '0', color: '#6b7280' }}>
                                  {slide.subtitle?.id || 'Subjudul belum diatur'}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                backgroundColor: slide.isActive ? '#dcfce7' : '#fef2f2',
                                color: slide.isActive ? '#166534' : '#991b1b'
                              }}>
                                {slide.isActive ? 'Aktif' : 'Nonaktif'}
                              </span>
                              <button
                                onClick={() => {
                                  setEditingSlide(slide);
                                  setSlideForm(slide);
                                  setShowEditSlideModal(true);
                                }}
                                style={{ ...secondaryButtonStyle, padding: '0.5rem' }}
                              >
                                <Edit style={{ width: '1rem', height: '1rem' }} />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm('Yakin ingin menghapus slide ini?')) {
                                    deleteSlide(slide.id);
                                  }
                                }}
                                style={{ ...dangerButtonStyle, padding: '0.5rem' }}
                              >
                                <Trash2 style={{ width: '1rem', height: '1rem' }} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add/Edit Slide Modal */}
                    {showEditSlideModal && (
                      <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '0.5rem',
                          padding: '2rem',
                          width: '90%',
                          maxWidth: '600px',
                          maxHeight: '80vh',
                          overflow: 'auto'
                        }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#111827' }}>
                            {editingSlide ? 'Edit Slide' : 'Tambah Slide Baru'}
                          </h3>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul (ID)</label>
                                <input
                                  type="text"
                                  value={slideForm?.title?.id || ''}
                                  onChange={(e) => setSlideForm({
                                    ...slideForm,
                                    title: {
                                      ...slideForm?.title,
                                      id: e.target.value
                                    }
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Masukkan judul dalam bahasa Indonesia"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul (EN)</label>
                                <input
                                  type="text"
                                  value={slideForm?.title?.en || ''}
                                  onChange={(e) => setSlideForm({
                                    ...slideForm,
                                    title: {
                                      ...slideForm?.title,
                                      en: e.target.value
                                    }
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Enter title in English"
                                />
                              </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Subjudul (ID)</label>
                                <input
                                  type="text"
                                  value={slideForm?.subtitle?.id || ''}
                                  onChange={(e) => setSlideForm({
                                    ...slideForm,
                                    subtitle: {
                                      ...slideForm?.subtitle,
                                      id: e.target.value
                                    }
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Masukkan subjudul dalam bahasa Indonesia"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Subjudul (EN)</label>
                                <input
                                  type="text"
                                  value={slideForm?.subtitle?.en || ''}
                                  onChange={(e) => setSlideForm({
                                    ...slideForm,
                                    subtitle: {
                                      ...slideForm?.subtitle,
                                      en: e.target.value
                                    }
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Enter subtitle in English"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Gambar Slide</label>
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Validate file size (max 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert('Ukuran file terlalu besar. Maksimal 5MB.');
                                      e.target.value = '';
                                      return;
                                    }
                                    
                                    // Validate file type
                                    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                    if (!validTypes.includes(file.type)) {
                                      alert('Format file tidak didukung. Gunakan JPG, PNG, GIF, atau WebP.');
                                      e.target.value = '';
                                      return;
                                    }
                                    
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      setSlideForm({
                                        ...slideForm,
                                        image: event.target?.result as string
                                      });
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                                Format: JPG, PNG, GIF, WebP. Maksimal 5MB.
                              </p>
                              {slideForm?.image && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <img 
                                    src={slideForm.image} 
                                    alt="Preview" 
                                    style={{ 
                                      width: '200px', 
                                      height: '120px', 
                                      objectFit: 'cover', 
                                      borderRadius: '0.375rem',
                                      border: '1px solid #d1d5db'
                                    }} 
                                  />
                                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>Preview gambar slide</p>
                                </div>
                              )}
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="checkbox"
                                id="slideActive"
                                checked={slideForm?.isActive || false}
                                onChange={(e) => setSlideForm({
                                  ...slideForm,
                                  isActive: e.target.checked
                                })}
                              />
                              <label htmlFor="slideActive" style={{ fontSize: '0.875rem', color: '#374151' }}>Aktifkan slide</label>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                              <button
                                onClick={() => {
                                  setShowEditSlideModal(false);
                                  setEditingSlide(null);
                                  setSlideForm({
                                    id: 0,
                                    title: { id: '', en: '' },
                                    subtitle: { id: '', en: '' },
                                    image: '',
                                    order: 1,
                                    isActive: true
                                  });
                                }}
                                style={{ ...secondaryButtonStyle }}
                              >
                                Batal
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    if (editingSlide) {
                                      await updateSlide(editingSlide.id, slideForm);
                                      showMessage('success', 'Slide berhasil diperbarui!');
                                    } else {
                                      await addSlide({
                                        title: slideForm.title,
                                        subtitle: slideForm.subtitle,
                                        image: slideForm.image,
                                        order: (slides?.length || 0) + 1,
                                        isActive: slideForm.isActive
                                      });
                                      showMessage('success', 'Slide berhasil ditambahkan!');
                                    }
                                    setShowEditSlideModal(false);
                                    setEditingSlide(null);
                                    setSlideForm({
                                      id: 0,
                                      title: { id: '', en: '' },
                                      subtitle: { id: '', en: '' },
                                      image: '',
                                      order: 1,
                                      isActive: true
                                    });
                                  } catch (error) {
                                    showMessage('error', 'Gagal menyimpan slide!');
                                  }
                                }}
                                style={{ ...primaryButtonStyle }}
                              >
                                {editingSlide ? 'Perbarui' : 'Tambah'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div>
                    {/* Profile Content Form */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#111827' }}>Informasi Profil Daerah</h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul Profil</label>
                            <input
                              type="text"
                              value={localProfileContent?.title || ''}
                              onChange={(e) => {
                                const newContent = {
                                  ...localProfileContent,
                                  title: e.target.value
                                };
                                setLocalProfileContent(newContent);
                                setHasUnsavedProfileChanges(true);
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                              placeholder="Contoh: Tentang Sukoharjo"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul Mengapa Investasi</label>
                            <input
                              type="text"
                              value={localProfileContent?.whyInvestTitle || ''}
                              onChange={(e) => {
                                const newContent = {
                                  ...localProfileContent,
                                  whyInvestTitle: e.target.value
                                };
                                setLocalProfileContent(newContent);
                                setHasUnsavedProfileChanges(true);
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                              placeholder="Contoh: Mengapa Berinvestasi di Sukoharjo?"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Subjudul Mengapa Investasi</label>
                          <input
                            type="text"
                            value={localProfileContent?.whyInvestSubtitle || ''}
                            onChange={(e) => {
                              const newContent = {
                                ...localProfileContent,
                                whyInvestSubtitle: e.target.value
                              };
                              setLocalProfileContent(newContent);
                              setHasUnsavedProfileChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="Contoh: Temukan keunggulan dan peluang investasi yang menarik"
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Deskripsi</label>
                          <textarea
                            value={localProfileContent?.description || ''}
                            onChange={(e) => {
                              setLocalProfileContent({
                                ...localProfileContent,
                                description: e.target.value
                              });
                              setHasUnsavedProfileChanges(true);
                            }}
                            rows={4}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              resize: 'vertical'
                            }}
                            placeholder="Masukkan deskripsi profil daerah..."
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Gambar Profil</label>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Validate file size (max 3MB)
                                if (file.size > 3 * 1024 * 1024) {
                                  alert('Ukuran file terlalu besar. Maksimal 3MB.');
                                  e.target.value = '';
                                  return;
                                }
                                
                                // Validate file type
                                const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                if (!validTypes.includes(file.type)) {
                                  alert('Format file tidak didukung. Gunakan JPG, PNG, GIF, atau WebP.');
                                  e.target.value = '';
                                  return;
                                }
                                
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  setLocalProfileContent({
                                    ...localProfileContent,
                                    image: event.target?.result as string
                                  });
                                  setHasUnsavedProfileChanges(true);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                            Format: JPG, PNG, GIF, WebP. Maksimal 3MB.
                          </p>
                          {localProfileContent?.image && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <img 
                                src={localProfileContent.image} 
                                alt="Preview Profil" 
                                style={{ 
                                  width: '150px', 
                                  height: '100px', 
                                  objectFit: 'cover', 
                                  borderRadius: '0.375rem',
                                  border: '1px solid #d1d5db'
                                }} 
                              />
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>Preview gambar profil daerah</p>
                            </div>
                          )}
                        </div>

                        {hasUnsavedProfileChanges && (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => {
                                updateProfileContent(localProfileContent);
                                setHasUnsavedProfileChanges(false);
                                showMessage('success', 'Profil berhasil disimpan!');
                              }}
                              style={{ ...primaryButtonStyle }}
                            >
                              Simpan Perubahan
                            </button>
                            <button
                              onClick={() => {
                                setLocalProfileContent(profileContent);
                                setHasUnsavedProfileChanges(false);
                              }}
                              style={{ ...secondaryButtonStyle }}
                            >
                              Batal
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Advantages Section */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '500', margin: '0', color: '#111827' }}>Keunggulan Daerah</h4>
                        <button
                          onClick={() => {
                            setAdvantageForm({
                              id: 0,
                              icon: 'fas fa-map-marker-alt',
                              title: '',
                              description: ''
                            });
                            setShowAddAdvantageModal(true);
                          }}
                          style={{ ...primaryButtonStyle }}
                        >
                          <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          Tambah Keunggulan
                        </button>
                      </div>

                      {!profileContent?.advantages || profileContent.advantages.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '3rem',
                          backgroundColor: '#f9fafb',
                          borderRadius: '0.5rem',
                          border: '2px dashed #d1d5db'
                        }}>
                          <Heart style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#6b7280' }}>Belum ada keunggulan daerah</h3>
                          <p style={{ margin: '0 0 1.5rem 0', color: '#9ca3af' }}>Tambahkan keunggulan pertama untuk menampilkan potensi daerah</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {profileContent.advantages.map((advantage, index) => (
                            <div key={index} style={{
                              backgroundColor: '#f9fafb',
                              padding: '1rem',
                              borderRadius: '0.5rem',
                              border: '1px solid #e5e7eb',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{ flex: 1 }}>
                                <h5 style={{ fontSize: '0.875rem', fontWeight: '500', margin: '0 0 0.25rem 0', color: '#111827' }}>
                                  {advantage.title}
                                </h5>
                                <p style={{ fontSize: '0.75rem', margin: '0', color: '#6b7280' }}>
                                  {advantage.description}
                                </p>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => {
                                    setAdvantageForm(advantage);
                                    setShowAddAdvantageModal(true);
                                  }}
                                  style={{ ...secondaryButtonStyle, padding: '0.5rem' }}
                                >
                                  <Edit style={{ width: '1rem', height: '1rem' }} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Yakin ingin menghapus keunggulan ini?')) {
                                      const updatedAdvantages = profileContent.advantages.filter((_, i) => i !== index);
                                      updateProfileContent({ ...profileContent, advantages: updatedAdvantages });
                                      showMessage('success', 'Keunggulan berhasil dihapus!');
                                    }
                                  }}
                                  style={{ ...dangerButtonStyle, padding: '0.5rem' }}
                                >
                                  <Trash2 style={{ width: '1rem', height: '1rem' }} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Add/Edit Advantage Modal */}
                    {showAddAdvantageModal && (
                      <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '0.5rem',
                          padding: '2rem',
                          width: '90%',
                          maxWidth: '500px'
                        }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#111827' }}>
                            {advantageForm.id ? 'Edit Keunggulan' : 'Tambah Keunggulan Baru'}
                          </h3>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul</label>
                              <input
                                type="text"
                                value={advantageForm?.title || ''}
                                onChange={(e) => setAdvantageForm({
                                  ...advantageForm,
                                  title: e.target.value
                                })}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                                placeholder="Contoh: Lokasi Strategis"
                              />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Deskripsi</label>
                              <textarea
                                value={advantageForm?.description || ''}
                                onChange={(e) => setAdvantageForm({
                                  ...advantageForm,
                                  description: e.target.value
                                })}
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  resize: 'vertical'
                                }}
                                placeholder="Masukkan deskripsi keunggulan..."
                              />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Icon Font Awesome</label>
                              <input
                                type="text"
                                value={advantageForm?.icon || 'fas fa-map-marker-alt'}
                                onChange={(e) => setAdvantageForm({
                                  ...advantageForm,
                                  icon: e.target.value
                                })}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                                placeholder="Contoh: fas fa-map-marker-alt"
                              />
                              <div style={{ 
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: '#f9fafb',
                                borderRadius: '0.375rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>Preview icon:</p>
                                <i className={advantageForm?.icon || 'fas fa-map-marker-alt'} style={{ fontSize: '1.5rem', color: '#4b5563' }}></i>
                                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.5rem 0 0 0' }}>
                                  Contoh: fas fa-home, fas fa-building, far fa-chart-bar, fab fa-facebook
                                </p>
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                              <button
                                onClick={() => {
                                  setShowAddAdvantageModal(false);
                                  setAdvantageForm({
                                    id: 0,
                                    icon: 'fas fa-map-marker-alt',
                                    title: '',
                                    description: ''
                                  });
                                }}
                                style={{ ...secondaryButtonStyle }}
                              >
                                Batal
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    if (advantageForm.id) {
                                      // Edit existing advantage
                                      await updateAdvantage(advantageForm.id, {
                                        title: advantageForm.title,
                                        description: advantageForm.description,
                                        icon: advantageForm.icon
                                      });
                                      showMessage('success', 'Keunggulan berhasil diperbarui!');
                                    } else {
                                      // Add new advantage
                                      await addAdvantage({
                                        icon: advantageForm.icon,
                                        title: advantageForm.title,
                                        description: advantageForm.description
                                      });
                                      showMessage('success', 'Keunggulan berhasil ditambahkan!');
                                    }
                                    setShowAddAdvantageModal(false);
                                    setAdvantageForm({
                                      id: 0,
                                      icon: 'fas fa-map-marker-alt',
                                      title: '',
                                      description: ''
                                    });
                                  } catch (error) {
                                    showMessage('error', 'Gagal menyimpan keunggulan!');
                                  }
                                }}
                                style={{ ...primaryButtonStyle }}
                              >
                                {advantageForm.id ? 'Perbarui' : 'Tambah'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Facilities Tab */}
                {activeTab === 'facilities' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0' }}>
                        {facilities?.length || 0} fasilitas tersedia, {facilities?.filter(f => f.isActive).length || 0} aktif
                      </p>
                      <button
                        onClick={() => {
                          setFacilityForm({
                            id: 0,
                            name: '',
                            description: '',
                            icon: 'fas fa-building',
                            category: 'Umum',
                            isActive: true
                          });
                          setShowAddFacilityModal(true);
                        }}
                        style={{ ...primaryButtonStyle }}
                      >
                        <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Tambah Fasilitas Baru
                      </button>
                    </div>

                    {/* Facilities List */}
                    {!facilities || facilities.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '3rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.5rem',
                        border: '2px dashed #d1d5db'
                      }}>
                        <Building style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#6b7280' }}>Belum ada fasilitas pendukung</h3>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#9ca3af' }}>Tambahkan fasilitas pertama untuk menampilkan infrastruktur daerah</p>
                        <button
                          onClick={() => {
                            setFacilityForm({
                              id: 0,
                              name: '',
                              description: '',
                              icon: 'fas fa-building',
                              category: 'Umum',
                              isActive: true
                            });
                            setShowAddFacilityModal(true);
                          }}
                          style={{ ...primaryButtonStyle }}
                        >
                          <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          Tambah Fasilitas Pertama
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                        {facilities.map((facility, index) => (
                          <div key={index} style={{
                            backgroundColor: 'white',
                            padding: '1.5rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                              <div style={{
                                width: '3rem',
                                height: '3rem',
                                backgroundColor: '#dbeafe',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '1rem'
                              }}>
                                <Building style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <h4 style={{ fontSize: '1rem', fontWeight: '500', margin: '0 0 0.25rem 0', color: '#111827' }}>
                                  {facility.name}
                                </h4>
                                <span style={{
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  backgroundColor: facility.isActive ? '#dcfce7' : '#fef2f2',
                                  color: facility.isActive ? '#166534' : '#991b1b'
                                }}>
                                  {facility.isActive ? 'Aktif' : 'Nonaktif'}
                                </span>
                              </div>
                            </div>
                            
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
                              {facility.description}
                            </p>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                                backgroundColor: '#f3f4f6',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem'
                              }}>
                                {facility.category || 'Umum'}
                              </span>
                              
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => {
                                    setFacilityForm(facility);
                                    setShowAddFacilityModal(true);
                                  }}
                                  style={{ ...secondaryButtonStyle, padding: '0.5rem' }}
                                >
                                  <Edit style={{ width: '1rem', height: '1rem' }} />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Yakin ingin menghapus fasilitas ini?')) {
                                      try {
                                        await deleteFacility(facility.id);
                                        showMessage('success', 'Fasilitas berhasil dihapus!');
                                      } catch (error) {
                                        showMessage('error', 'Gagal menghapus fasilitas!');
                                      }
                                    }
                                  }}
                                  style={{ ...dangerButtonStyle, padding: '0.5rem' }}
                                >
                                  <Trash2 style={{ width: '1rem', height: '1rem' }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add/Edit Facility Modal */}
                    {showAddFacilityModal && (
                      <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '0.5rem',
                          padding: '2rem',
                          width: '90%',
                          maxWidth: '600px'
                        }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#111827' }}>
                            {facilityForm.id ? 'Edit Fasilitas' : 'Tambah Fasilitas Baru'}
                          </h3>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Nama Fasilitas</label>
                                <input
                                  type="text"
                                  value={facilityForm?.name || ''}
                                  onChange={(e) => setFacilityForm({
                                    ...facilityForm,
                                    name: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Contoh: Jalan Tol Solo-Sragen"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Kategori</label>
                                <select
                                  value={facilityForm?.category || 'Umum'}
                                  onChange={(e) => setFacilityForm({
                                    ...facilityForm,
                                    category: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  <option value="Transportasi">Transportasi</option>
                                  <option value="Industri">Industri</option>
                                  <option value="Pendidikan">Pendidikan</option>
                                  <option value="Kesehatan">Kesehatan</option>
                                  <option value="Energi">Energi</option>
                                  <option value="Telekomunikasi">Telekomunikasi</option>
                                  <option value="SDM">SDM</option>
                                  <option value="Umum">Umum</option>
                                </select>
                              </div>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Deskripsi</label>
                              <textarea
                                value={facilityForm?.description || ''}
                                onChange={(e) => setFacilityForm({
                                  ...facilityForm,
                                  description: e.target.value
                                })}
                                rows={4}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  resize: 'vertical'
                                }}
                                placeholder="Masukkan deskripsi fasilitas..."
                              />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Icon Font Awesome</label>
                              <input
                                type="text"
                                value={facilityForm?.icon || 'fas fa-building'}
                                onChange={(e) => setFacilityForm({
                                  ...facilityForm,
                                  icon: e.target.value
                                })}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                                placeholder="Contoh: fas fa-building"
                              />
                              <div style={{ 
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: '#f9fafb',
                                borderRadius: '0.375rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>Preview icon:</p>
                                <i className={facilityForm?.icon || 'fas fa-building'} style={{ fontSize: '1.5rem', color: '#4b5563' }}></i>
                                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.5rem 0 0 0' }}>
                                  Contoh: fas fa-car, fas fa-hospital, fas fa-school, fas fa-wifi
                                </p>
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="checkbox"
                                id="facilityActive"
                                checked={facilityForm?.isActive || false}
                                onChange={(e) => setFacilityForm({
                                  ...facilityForm,
                                  isActive: e.target.checked
                                })}
                              />
                              <label htmlFor="facilityActive" style={{ fontSize: '0.875rem', color: '#374151' }}>Aktifkan fasilitas</label>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                              <button
                                onClick={() => {
                                  setShowAddFacilityModal(false);
                                  setFacilityForm({
                                    id: 0,
                                    name: '',
                                    description: '',
                                    icon: 'fas fa-building',
                                    category: 'Umum',
                                    isActive: true
                                  });
                                }}
                                style={{ ...secondaryButtonStyle }}
                              >
                                Batal
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    if (facilityForm.id) {
                                      // Edit existing facility
                                      await updateFacility(facilityForm.id, {
                                        name: facilityForm.name,
                                        description: facilityForm.description,
                                        icon: facilityForm.icon,
                                        category: facilityForm.category,
                                        isActive: facilityForm.isActive
                                      });
                                      showMessage('success', 'Fasilitas berhasil diperbarui!');
                                    } else {
                                      // Add new facility
                                      await addFacility({
                                        name: facilityForm.name,
                                        description: facilityForm.description,
                                        icon: facilityForm.icon,
                                        category: facilityForm.category,
                                        isActive: facilityForm.isActive
                                      });
                                      showMessage('success', 'Fasilitas berhasil ditambahkan!');
                                    }
                                    setShowAddFacilityModal(false);
                                    setFacilityForm({
                                      id: 0,
                                      name: '',
                                      description: '',
                                      icon: 'fas fa-building',
                                      category: 'Umum',
                                      isActive: true
                                    });
                                  } catch (error) {
                                    showMessage('error', 'Gagal menyimpan fasilitas!');
                                  }
                                }}
                                style={{ ...primaryButtonStyle }}
                              >
                                {facilityForm.id ? 'Perbarui' : 'Tambah'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Investment Opportunities Tab - Simple Management */}
                {activeTab === 'opportunities' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0' }}>
                        {investmentOpportunities?.length || 0} potensi investasi tersedia, {investmentOpportunities?.filter(o => o.isActive).length || 0} aktif
                      </p>
                      <button
                        onClick={() => {
                          setOpportunityForm({
                            id: 0,
                            title: '',
                            description: '',
                            image: '',
                            sector: '',
                            investmentRange: '',
                            roi: '',
                            location: '',
                            isActive: true
                          });
                          setShowAddOpportunityModal(true);
                        }}
                        style={{ ...primaryButtonStyle }}
                      >
                        <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Tambah Potensi Investasi Baru
                      </button>
                    </div>

                    {/* Opportunities List */}
                    {!investmentOpportunities || investmentOpportunities.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '3rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.5rem',
                        border: '2px dashed #d1d5db'
                      }}>
                        <TrendingUp style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#6b7280' }}>Belum ada potensi investasi</h3>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#9ca3af' }}>Tambahkan potensi investasi pertama untuk menampilkan peluang di daerah</p>
                        <button
                          onClick={() => {
                            setOpportunityForm({
                              id: 0,
                              title: '',
                              description: '',
                              image: '',
                              sector: '',
                              investmentRange: '',
                              roi: '',
                              location: '',
                              isActive: true
                            });
                            setShowAddOpportunityModal(true);
                          }}
                          style={{ ...primaryButtonStyle }}
                        >
                          <Plus style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                          Tambah Potensi Investasi Pertama
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                        {investmentOpportunities.map((opportunity, index) => (
                          <div key={index} style={{
                            backgroundColor: 'white',
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb'
                          }}>
                            {opportunity.image && (
                              <div style={{
                                width: '100%',
                                height: '160px',
                                backgroundImage: `url(${opportunity.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#f3f4f6'
                              }} />
                            )}
                            <div style={{ padding: '1.25rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0', color: '#111827' }}>
                                  {opportunity.title}
                                </h4>
                                <span style={{
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  backgroundColor: opportunity.isActive ? '#dcfce7' : '#fef2f2',
                                  color: opportunity.isActive ? '#166534' : '#991b1b'
                                }}>
                                  {opportunity.isActive ? 'Aktif' : 'Nonaktif'}
                                </span>
                              </div>
                              
                              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', lineHeight: '1.5' }}>
                                {opportunity.description}
                              </p>
                              
                              <div style={{ marginBottom: '1rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <div>
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '500' }}>Sektor:</span>
                                    <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0' }}>{opportunity.sector}</p>
                                  </div>
                                  <div>
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '500' }}>Lokasi:</span>
                                    <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0' }}>{opportunity.location}</p>
                                  </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                  <div>
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '500' }}>Investasi:</span>
                                    <p style={{ fontSize: '0.875rem', color: '#374151', margin: '0', fontWeight: '600' }}>{opportunity.investmentRange}</p>
                                  </div>
                                  <div>
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '500' }}>ROI:</span>
                                    <p style={{ fontSize: '0.875rem', color: '#10b981', margin: '0', fontWeight: '600' }}>{opportunity.roi}</p>
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => {
                                    setOpportunityForm(opportunity);
                                    setShowAddOpportunityModal(true);
                                  }}
                                  style={{ ...secondaryButtonStyle, padding: '0.5rem', flex: 1 }}
                                >
                                  <Edit style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                                  Edit
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Yakin ingin menghapus potensi investasi ini?')) {
                                      try {
                                        await deleteOpportunity(opportunity.id);
                                        showMessage('success', 'Potensi investasi berhasil dihapus!');
                                      } catch (error) {
                                        showMessage('error', 'Gagal menghapus potensi investasi!');
                                      }
                                    }
                                  }}
                                  style={{ ...dangerButtonStyle, padding: '0.5rem' }}
                                >
                                  <Trash2 style={{ width: '1rem', height: '1rem' }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add/Edit Opportunity Modal */}
                    {showAddOpportunityModal && (
                      <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '0.5rem',
                          padding: '2rem',
                          width: '90%',
                          maxWidth: '700px',
                          maxHeight: '90vh',
                          overflow: 'auto'
                        }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#111827' }}>
                            {opportunityForm.id ? 'Edit Potensi Investasi' : 'Tambah Potensi Investasi Baru'}
                          </h3>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Judul</label>
                                <input
                                  type="text"
                                  value={opportunityForm?.title || ''}
                                  onChange={(e) => setOpportunityForm({
                                    ...opportunityForm,
                                    title: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Contoh: Industri Gitar Baki"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Sektor</label>
                                <input
                                  type="text"
                                  value={opportunityForm?.sector || ''}
                                  onChange={(e) => setOpportunityForm({
                                    ...opportunityForm,
                                    sector: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Contoh: Manufaktur"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Deskripsi</label>
                              <textarea
                                value={opportunityForm?.description || ''}
                                onChange={(e) => setOpportunityForm({
                                  ...opportunityForm,
                                  description: e.target.value
                                })}
                                rows={4}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  resize: 'vertical'
                                }}
                                placeholder="Masukkan deskripsi potensi investasi..."
                              />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Gambar</label>
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Validate file size (max 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert('Ukuran file terlalu besar. Maksimal 5MB.');
                                      e.target.value = '';
                                      return;
                                    }
                                    
                                    // Validate file type
                                    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                    if (!validTypes.includes(file.type)) {
                                      alert('Format file tidak didukung. Gunakan JPG, PNG, GIF, atau WebP.');
                                      e.target.value = '';
                                      return;
                                    }
                                    
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      setOpportunityForm({
                                        ...opportunityForm,
                                        image: event.target?.result as string
                                      });
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                                Format: JPG, PNG, GIF, WebP. Maksimal 5MB.
                              </p>
                              {opportunityForm?.image && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <img 
                                    src={opportunityForm.image} 
                                    alt="Preview" 
                                    style={{ 
                                      width: '200px', 
                                      height: '120px', 
                                      objectFit: 'cover', 
                                      borderRadius: '0.375rem',
                                      border: '1px solid #d1d5db'
                                    }} 
                                  />
                                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>Preview gambar investasi</p>
                                </div>
                              )}
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Range Investasi</label>
                                <input
                                  type="text"
                                  value={opportunityForm?.investmentRange || ''}
                                  onChange={(e) => setOpportunityForm({
                                    ...opportunityForm,
                                    investmentRange: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Rp 5-50 Miliar"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>ROI per Tahun</label>
                                <input
                                  type="text"
                                  value={opportunityForm?.roi || ''}
                                  onChange={(e) => setOpportunityForm({
                                    ...opportunityForm,
                                    roi: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="15-25% per tahun"
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Lokasi</label>
                                <input
                                  type="text"
                                  value={opportunityForm?.location || ''}
                                  onChange={(e) => setOpportunityForm({
                                    ...opportunityForm,
                                    location: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                  }}
                                  placeholder="Kecamatan Baki"
                                />
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="checkbox"
                                id="opportunityActive"
                                checked={opportunityForm?.isActive || false}
                                onChange={(e) => setOpportunityForm({
                                  ...opportunityForm,
                                  isActive: e.target.checked
                                })}
                              />
                              <label htmlFor="opportunityActive" style={{ fontSize: '0.875rem', color: '#374151' }}>Aktifkan potensi investasi</label>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                              <button
                                onClick={() => {
                                  setShowAddOpportunityModal(false);
                                  setOpportunityForm({
                                    id: 0,
                                    title: '',
                                    description: '',
                                    image: '',
                                    sector: '',
                                    investmentRange: '',
                                    roi: '',
                                    location: '',
                                    isActive: true
                                  });
                                }}
                                style={{ ...secondaryButtonStyle }}
                              >
                                Batal
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    if (opportunityForm.id) {
                                      // Edit existing opportunity
                                      await updateOpportunity(opportunityForm.id, {
                                        title: opportunityForm.title,
                                        description: opportunityForm.description,
                                        image: opportunityForm.image,
                                        sector: opportunityForm.sector,
                                        investmentRange: opportunityForm.investmentRange,
                                        roi: opportunityForm.roi,
                                        location: opportunityForm.location,
                                        isActive: opportunityForm.isActive
                                      });
                                      showMessage('success', 'Potensi investasi berhasil diperbarui!');
                                    } else {
                                      // Add new opportunity
                                      await addOpportunity({
                                        title: opportunityForm.title,
                                        description: opportunityForm.description,
                                        image: opportunityForm.image,
                                        sector: opportunityForm.sector,
                                        investmentRange: opportunityForm.investmentRange,
                                        roi: opportunityForm.roi,
                                        location: opportunityForm.location,
                                        isActive: opportunityForm.isActive
                                      });
                                      showMessage('success', 'Potensi investasi berhasil ditambahkan!');
                                    }
                                    setShowAddOpportunityModal(false);
                                    setOpportunityForm({
                                      id: 0,
                                      title: '',
                                      description: '',
                                      image: '',
                                      sector: '',
                                      investmentRange: '',
                                      roi: '',
                                      location: '',
                                      isActive: true
                                    });
                                  } catch (error) {
                                    showMessage('error', 'Gagal menyimpan potensi investasi!');
                                  }
                                }}
                                style={{ ...primaryButtonStyle }}
                              >
                                {opportunityForm.id ? 'Perbarui' : 'Tambah'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Footer Tab */}
                {activeTab === 'footer' && (
                  <div>
                    <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#111827' }}>Informasi Kontak Footer</h4>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Nama Instansi</label>
                          <input
                            type="text"
                            value={localFooterContent?.contactInfo?.office || ''}
                            onChange={(e) => {
                              setLocalFooterContent({
                                ...localFooterContent,
                                contactInfo: {
                                  ...localFooterContent?.contactInfo,
                                  office: e.target.value
                                }
                              });
                              setHasUnsavedFooterChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="DPMPTSP Sukoharjo"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Email</label>
                          <input
                            type="email"
                            value={localFooterContent?.contactInfo?.email || ''}
                            onChange={(e) => {
                              setLocalFooterContent({
                                ...localFooterContent,
                                contactInfo: {
                                  ...localFooterContent?.contactInfo,
                                  email: e.target.value
                                }
                              });
                              setHasUnsavedFooterChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="dpmptsp@sukoharjokab.go.id"
                          />
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Telepon</label>
                          <input
                            type="text"
                            value={localFooterContent?.contactInfo?.phone || ''}
                            onChange={(e) => {
                              setLocalFooterContent({
                                ...localFooterContent,
                                contactInfo: {
                                  ...localFooterContent?.contactInfo,
                                  phone: e.target.value
                                }
                              });
                              setHasUnsavedFooterChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="+62 271 593156"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Jam Operasional</label>
                          <input
                            type="text"
                            value={localFooterContent?.contactInfo?.officeHours || ''}
                            onChange={(e) => {
                              setLocalFooterContent({
                                ...localFooterContent,
                                contactInfo: {
                                  ...localFooterContent?.contactInfo,
                                  officeHours: e.target.value
                                }
                              });
                              setHasUnsavedFooterChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="Senin - Jumat: 08:00 - 16:00 WIB"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Alamat</label>
                        <textarea
                          value={localFooterContent?.contactInfo?.address || ''}
                          onChange={(e) => {
                            setLocalFooterContent({
                              ...localFooterContent,
                              contactInfo: {
                                ...localFooterContent?.contactInfo,
                                address: e.target.value
                              }
                            });
                            setHasUnsavedFooterChanges(true);
                          }}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            resize: 'vertical'
                          }}
                          placeholder="Jl. Jend. Sudirman No. 1, Sukoharjo, Jawa Tengah 57512"
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Deskripsi</label>
                        <textarea
                          value={localFooterContent?.description || ''}
                          onChange={(e) => {
                            setLocalFooterContent({
                              ...localFooterContent,
                              description: e.target.value
                            });
                            setHasUnsavedFooterChanges(true);
                          }}
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            resize: 'vertical'
                          }}
                          placeholder="Deskripsi singkat tentang instansi..."
                        />
                      </div>

                      {hasUnsavedFooterChanges && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => {
                              updateFooterContent(localFooterContent);
                              setHasUnsavedFooterChanges(false);
                              showMessage('success', 'Footer berhasil disimpan!');
                            }}
                            style={{ ...primaryButtonStyle }}
                          >
                            Simpan Perubahan
                          </button>
                          <button
                            onClick={() => {
                              setLocalFooterContent(footerContent);
                              setHasUnsavedFooterChanges(false);
                            }}
                            style={{ ...secondaryButtonStyle }}
                          >
                            Batal
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div>
                    <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#111827' }}>Pengaturan Website</h4>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Nama Website</label>
                          <input
                            type="text"
                            value={localWebsiteSettings?.siteName || ''}
                            onChange={(e) => {
                              setLocalWebsiteSettings({
                                ...localWebsiteSettings,
                                siteName: e.target.value
                              });
                              setHasUnsavedSettingsChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="Ruang Investasi Sukoharjo"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Tagline</label>
                          <input
                            type="text"
                            value={localWebsiteSettings?.tagline || ''}
                            onChange={(e) => {
                              setLocalWebsiteSettings({
                                ...localWebsiteSettings,
                                tagline: e.target.value
                              });
                              setHasUnsavedSettingsChanges(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                            placeholder="Investment Gateway to Prosperity"
                          />
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Warna Primer</label>
                          <input
                            type="color"
                            value={localWebsiteSettings?.primaryColor || '#1e3a8a'}
                            onChange={(e) => {
                              setLocalWebsiteSettings({
                                ...localWebsiteSettings,
                                primaryColor: e.target.value
                              });
                              setHasUnsavedSettingsChanges(true);
                            }}
                            style={{
                              width: '100%',
                              height: '2.5rem',
                              padding: '0.25rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Warna Sekunder</label>
                          <input
                            type="color"
                            value={localWebsiteSettings?.secondaryColor || '#059669'}
                            onChange={(e) => {
                              setLocalWebsiteSettings({
                                ...localWebsiteSettings,
                                secondaryColor: e.target.value
                              });
                              setHasUnsavedSettingsChanges(true);
                            }}
                            style={{
                              width: '100%',
                              height: '2.5rem',
                              padding: '0.25rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem'
                            }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Logo Website</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              // Validate file size (max 2MB)
                              if (file.size > 2 * 1024 * 1024) {
                                alert('Ukuran file terlalu besar. Maksimal 2MB.');
                                e.target.value = '';
                                return;
                              }
                              
                              // Validate file type
                              const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
                              if (!validTypes.includes(file.type)) {
                                alert('Format file tidak didukung. Gunakan JPG, PNG, GIF, WebP, atau SVG.');
                                e.target.value = '';
                                return;
                              }
                              
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setLocalWebsiteSettings({
                                  ...localWebsiteSettings,
                                  logo: event.target?.result as string
                                });
                                setHasUnsavedSettingsChanges(true);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                          }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                          Format: JPG, PNG, GIF, WebP, SVG. Maksimal 2MB.
                        </p>
                        {localWebsiteSettings?.logo && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <img 
                              src={localWebsiteSettings.logo} 
                              alt="Logo Preview" 
                              style={{ 
                                width: '120px', 
                                height: '80px', 
                                objectFit: 'contain',
                                borderRadius: '0.375rem',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#f9fafb'
                              }} 
                            />
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>Preview logo website</p>
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>Logo Admin (untuk dashboard)</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) {
                                alert('Ukuran file terlalu besar. Maksimal 2MB.');
                                e.target.value = '';
                                return;
                              }
                              const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
                              if (!validTypes.includes(file.type)) {
                                alert('Format file tidak didukung. Gunakan JPG, PNG, GIF, WebP, atau SVG.');
                                e.target.value = '';
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setLocalWebsiteSettings({
                                  ...localWebsiteSettings,
                                  adminLogo: event.target?.result as string
                                });
                                setHasUnsavedSettingsChanges(true);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                          }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                          Format: JPG, PNG, GIF, WebP, SVG. Maksimal 2MB.
                        </p>
                        {(localWebsiteSettings?.adminLogo || localWebsiteSettings?.logo) && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <img 
                              src={localWebsiteSettings.adminLogo || localWebsiteSettings.logo} 
                              alt="Admin Logo Preview" 
                              style={{ 
                                width: '120px', 
                                height: '80px', 
                                objectFit: 'contain',
                                borderRadius: '0.375rem',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#f9fafb'
                              }} 
                            />
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>Preview logo admin</p>
                          </div>
                        )}
                      </div>

                      {hasUnsavedSettingsChanges && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => {
                              updateWebsiteSettings(localWebsiteSettings);
                              setHasUnsavedSettingsChanges(false);
                              showMessage('success', 'Pengaturan berhasil disimpan!');
                            }}
                            style={{ ...primaryButtonStyle }}
                          >
                            Simpan Perubahan
                          </button>
                          <button
                            onClick={() => {
                              setLocalWebsiteSettings(websiteSettings);
                              setHasUnsavedSettingsChanges(false);
                            }}
                            style={{ ...secondaryButtonStyle }}
                          >
                            Batal
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contacts Management Section */}
          {activeSection === 'contacts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 1rem 0' }}>
                  Manajemen Kontak
                </h2>
                <p style={{ color: '#6b7280', margin: '0' }}>
                  Kelola pertanyaan dan pesan dari pengunjung website
                </p>
              </div>

              {/* Contact Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Mail style={{ height: '2rem', width: '2rem', color: '#2563eb', marginRight: '1rem' }} />
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>
                        Total Pesan
                      </p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        0
                      </p>
                    </div>
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Eye style={{ height: '2rem', width: '2rem', color: '#d97706', marginRight: '1rem' }} />
                    <div>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', margin: '0' }}>
                        Belum Dibaca
                      </p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                        0
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact List Placeholder */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '3rem',
                textAlign: 'center'
              }}>
                <MessageSquare style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                <h3 style={{ fontSize: '1.125rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#6b7280' }}>Belum ada pesan kontak</h3>
                <p style={{ margin: '0', color: '#9ca3af' }}>Pesan dari pengunjung akan muncul di sini</p>
              </div>
            </div>
          )}

          {/* Data Management Section */}
          {activeSection === 'data' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: '0 0 1rem 0' }}>
                  Manajemen Data
                </h2>
                <p style={{ color: '#6b7280', margin: '0' }}>
                  Import, export, dan kelola data sistem
                </p>
              </div>

              {/* Data Management Actions */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', margin: '0 0 1rem 0' }}>
                  Aksi Data
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <button
                    onClick={async () => {
                      if (window.confirm('Buat data contoh? Ini akan menambahkan data demo ke sistem.')) {
                        try {
                          await seedAPI.seedAll();
                          await refreshContent(); // Refresh the content after seeding
                          showMessage('success', 'Data contoh berhasil dibuat!');
                        } catch (error) {
                          console.error('Error creating sample data:', error);
                          showMessage('error', 'Gagal membuat data contoh!');
                        }
                      }
                    }}
                    style={{
                      ...primaryButtonStyle,
                      backgroundColor: '#16a34a'
                    }}
                  >
                    <Database style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                    Buat Data Contoh
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        const response = await excelAPI.downloadTemplate();
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'poi_template.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        showMessage('success', 'Template berhasil diunduh!');
                      } catch (error) {
                        console.error('Error downloading template:', error);
                        showMessage('error', 'Gagal mengunduh template!');
                      }
                    }}
                    style={{ ...primaryButtonStyle }}
                  >
                    <Download style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                    Download Template
                  </button>
                  
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.xlsx,.xls,.csv';
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          try {
                            const result = await excelAPI.importPOIs(file);
                            await refreshContent(); // Refresh the content after import
                            if (result.data) {
                              showMessage('success', `Import berhasil! ${result.data.created} data baru dibuat, ${result.data.updated} data diperbarui.`);
                              setImportResults(result.data);
                            } else {
                              showMessage('success', 'Import berhasil!');
                            }
                          } catch (error) {
                            console.error('Error importing data:', error);
                            showMessage('error', 'Gagal mengimport data!');
                          }
                        }
                      };
                      input.click();
                    }}
                    style={{ ...primaryButtonStyle }}
                  >
                    <Upload style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                    Import Data
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        const response = await excelAPI.exportPOIs();
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `poi_export_${new Date().toISOString().split('T')[0]}.xlsx`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        showMessage('success', 'Data berhasil diekspor!');
                      } catch (error) {
                        console.error('Error exporting data:', error);
                        showMessage('error', 'Gagal mengekspor data!');
                      }
                    }}
                    style={{
                      ...primaryButtonStyle,
                      backgroundColor: '#dc2626'
                    }}
                  >
                    <FileSpreadsheet style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                    Export Data
                  </button>
                </div>
              </div>

              {/* KML Files Section */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', margin: '0 0 1rem 0' }}>
                  File KML
                </h3>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  <Map style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#d1d5db' }} />
                  <p style={{ margin: '0' }}>Belum ada file KML yang diupload</p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SimpleUnifiedDashboard;